import { createFileRoute, useHydrated, useNavigate } from '@tanstack/react-router';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';
import { useCategoryOptions } from '@/lib/category-labels';
import { MainSectionHeader, MainSectionContent } from '@/routes/ngos/-components/main-section';
import { VerticalTabs } from '@/routes/ngos/-components/vertical-tabs';
import { FormHandleProvider, useRegisterFormHandle } from '@/routes/ngos/-components/form-context';
import { EditToggleButton } from '@/routes/ngos/-components/edit-toggle-button';
import { EDIT_MODE_TOGGLE_ENABLED } from '@/lib/feature-flags';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@worldcoo/ui/components/card';
import { useNgoProfileQuery } from '@/lib/data-hooks';
import { trpc } from '@/lib/trpc';
import { useAppForm } from '@/lib/use-app-form';
import { TRPCClientError } from '@trpc/client';
import { FormReadOnlyProvider } from '@worldcoo/ui/components/form';
import { useEffect, useState } from 'react';
import { SettingsContactsTab } from './-components/settings-contacts-tab';
import { SettingsDonationsTab } from './-components/settings-donations-tab';
import { SettingsUsersTab } from './-components/settings-users-tab';
import { SettingsContractsTab } from './-components/settings-contracts-tab';
import { showsAgreementsTab } from './-components/contracts/agreements-tab';
import { EditFooter } from './-components/edit-footer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getLocale } from '@/lib/locale-utils';
import { uploadNgoFiles, UnsupportedImageFileError } from '@/lib/upload-to-s3';
import { settingsFormOpts } from './-components/settings-form.opts';
import { useUploadTexts } from './-components/use-upload-texts';
import { settingsFormSchema } from '@/lib/onboarding-schemas';
import { evaluateBankCertificateRule } from '@/lib/bank-certificate-rule';
import { NgoLanguageSelect } from './-components/ngo-language-select';

const tabValues = [
	'organization',
	'contacts',
	'users',
	'volunteering',
	'privacy',
	'donations',
	'language',
	'contracts',
] as const;
type TabValue = (typeof tabValues)[number];

// TODO: we need to find a better way to associate form fields with tabs, especially for error handling. This hardcoded mapping is not scalable and error-prone.
const fieldToTab: Record<string, TabValue> = {
	name: 'organization',
	web: 'organization',
	taxId: 'organization',
	address: 'organization',
	city: 'organization',
	postalCode: 'organization',
	country: 'organization',
	region: 'organization',
	province: 'organization',
	privacyUrl: 'organization',
	actionScope: 'organization',
	countries: 'organization',
	logo: 'organization',
	certifiedByFoundation: 'organization',
	certifiedByFoundationDescription: 'organization',
	contact1Firstname: 'contacts',
	contact1Lastname: 'contacts',
	contact1Position: 'contacts',
	contact1Email: 'contacts',
	contact1Phone: 'contacts',
	contact2Firstname: 'contacts',
	contact2Lastname: 'contacts',
	contact2Position: 'contacts',
	contact2Email: 'contacts',
	contact2Phone: 'contacts',
	contactLegalFirstname: 'contacts',
	contactLegalLastname: 'contacts',
	contactLegalPosition: 'contacts',
	contactLegalId: 'contacts',
	contactLegalEmail: 'contacts',
	language: 'contacts',
	allowedEmails: 'users',
	corporateVolunteering: 'volunteering',
	corporateVolunteeringDescription: 'volunteering',
	donationCertificates: 'donations',
	bankInfo: 'donations',
	bankCurrency: 'donations',
	bankAccountName: 'donations',
	bankAccountCertificate: 'donations',
	bankAddress: 'donations',
	bankCity: 'donations',
	bankPostalCode: 'donations',
	bankCountry: 'donations',
	bankRegion: 'donations',
	bankProvince: 'donations',
	privacyEmail: 'privacy',
	privacyPhone: 'privacy',
	privacyAddress: 'privacy',
	privacyCity: 'privacy',
	privacyPostalCode: 'privacy',
	privacyCountry: 'privacy',
	privacyRegion: 'privacy',
	privacyProvince: 'privacy',
};

function ErrorDot({ active }: { active: boolean }) {
	return <span className={`size-2 rounded-full ${active ? 'bg-destructive-foreground' : 'bg-destructive'}`} />;
}

/** The same blue the portal already uses for an informative notice, not the red of a validation error. */
function PendingDot({ active }: { active: boolean }) {
	return <span className={`size-2 rounded-full ${active ? 'bg-white' : 'bg-blue-600'}`} />;
}

export const Route = createFileRoute('/ngos/settings')({
	validateSearch: v.object({
		tab: v.fallback(v.optional(v.picklist(tabValues)), undefined),
		editing: v.fallback(v.optional(v.boolean()), undefined),
	}),
	loader: ({ context }) => {
		context.queryClient.prefetchQuery(trpc.ngo.getProfile.queryOptions());
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useLingui();
	const { tab, editing } = Route.useSearch();
	const navigate = useNavigate({ from: '/ngos/settings' });
	const activeTab: TabValue = tab ?? 'organization';
	const isEditing = !EDIT_MODE_TOGGLE_ENABLED || editing === true;

	return (
		<FormHandleProvider>
			<MainSectionHeader
				title={<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Settings`}</h1>}
				actions={
					EDIT_MODE_TOGGLE_ENABLED && activeTab !== 'language' && activeTab !== 'contracts' ? (
						<EditToggleButton isEditing={isEditing} navigate={navigate} />
					) : null
				}
			/>
			<MainSectionContent className="md:!flex-row md:gap-12 md:!overflow-hidden">
				<SettingsContent />
			</MainSectionContent>
			<div id="settings-footer-portal" />
		</FormHandleProvider>
	);
}

function SettingsContent() {
	const { t, i18n } = useLingui();
	const { tab, editing } = Route.useSearch();
	const activeTab = tab ?? 'organization';
	const isEditing = !EDIT_MODE_TOGGLE_ENABLED || editing === true;
	const [tabsWithErrors, setTabsWithErrors] = useState(new Set<TabValue>());

	useEffect(() => {
		if (!isEditing) setTabsWithErrors(new Set());
	}, [isEditing]);

	const ngo = useNgoProfileQuery();
	const { data: agreements } = useQuery(trpc.contract.ngoContracts.queryOptions());
	const hydrated = useHydrated();

	/**
	 * Changing who binds the NGO is an act somebody takes responsibility for, so it is not saved without
	 * the confirmation beside it (FR-70).
	 */
	const awaitsConfirmation = (value: {
		contactLegalFirstname: string;
		contactLegalLastname: string;
		contactLegalPosition: string;
		contactLegalId: string;
		contactLegalEmail: string;
		legalRepresentativeConfirmed: boolean;
	}) =>
		ngo.hasEnteredPlanB &&
		!value.legalRepresentativeConfirmed &&
		(value.contactLegalFirstname !== (ngo.contactLegalFirstname ?? '') ||
			value.contactLegalLastname !== (ngo.contactLegalLastname ?? '') ||
			value.contactLegalPosition !== (ngo.contactLegalPosition ?? '') ||
			value.contactLegalId !== (ngo.contactLegalId ?? '') ||
			value.contactLegalEmail !== (ngo.contactLegalEmail ?? ''));

	const errorHint = (tabKey: TabValue) =>
		tabsWithErrors.has(tabKey) ? (active: boolean) => <ErrorDot active={active} /> : undefined;

	// An error outranks the reminder: a tab cannot carry two dots, and the red one needs acting on first.
	const contactsHint =
		errorHint('contacts') ??
		(ngo.awaitsLegalRepresentative ? (active: boolean) => <PendingDot active={active} /> : undefined);

	const tabConfig = [
		{ value: 'organization' as const, label: t`Entity details`, hint: errorHint('organization') },
		{ value: 'contacts' as const, label: t`Contact Person`, hint: contactsHint },
		{ value: 'users' as const, label: t`Users with access`, hint: errorHint('users') },
		{ value: 'volunteering' as const, label: t`Corporate volunteering`, hint: errorHint('volunteering') },
		{ value: 'privacy' as const, label: t`Data protection`, hint: errorHint('privacy') },
		{ value: 'donations' as const, label: t`Bank details`, hint: errorHint('donations') },
		{ value: 'language' as const, label: t`Language` },
		...(showsAgreementsTab({ activeTab, hydrated, agreementCount: agreements?.length ?? 0 })
			? [{ value: 'contracts' as const, label: t`Agreements` }]
			: []),
	];
	const countryFieldTexts = {
		search: t`Search...`,
		popular: t`Popular`,
		allCountries: t`All countries`,
		noCountryFound: t`No country found.`,
	};
	const regionFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const provinceFieldTexts = { search: t`Search...`, noResults: t`No results found.` };
	const navigate = useNavigate({ from: '/ngos/settings' });
	const queryClient = useQueryClient();
	const mutation = useMutation(trpc.ngo.putProfile.mutationOptions());
	const designate = useMutation(trpc.ngo.designateLegalRepresentative.mutationOptions());

	const actionScopeOptions = useCategoryOptions();

	const [showContact2, setShowContact2] = useState(
		!!(ngo.contact2Firstname || ngo.contact2Lastname || ngo.contact2Email),
	);

	const form = useAppForm({
		...settingsFormOpts,
		defaultValues: {
			name: ngo.name ?? '',
			web: ngo.web ?? '',
			taxId: ngo.taxId ?? '',
			address: ngo.address ?? '',
			city: ngo.city ?? '',
			postalCode: ngo.postalCode ?? '',
			country: ngo.country ?? '',
			region: ngo.region ?? '',
			province: ngo.province ?? '',
			privacyUrl: ngo.privacyUrl ?? '',
			actionScope: ngo.actionScope ?? [],
			countries: ngo.countries ?? [],
			logo: undefined as File | '' | undefined,

			allowedEmails: (ngo.allowedEmails ?? []).map((e) => ({ _key: crypto.randomUUID() as string, email: e })),
			contact1Firstname: ngo.contact1Firstname ?? '',
			contact1Lastname: ngo.contact1Lastname ?? '',
			contact1Position: ngo.contact1Position ?? '',
			contact1Email: ngo.contact1Email ?? '',
			contact1Phone: ngo.contact1Phone ?? '',
			contact2Firstname: ngo.contact2Firstname ?? '',
			contact2Lastname: ngo.contact2Lastname ?? '',
			contact2Position: ngo.contact2Position ?? '',
			contact2Email: ngo.contact2Email ?? '',
			contact2Phone: ngo.contact2Phone ?? '',
			contactLegalFirstname: ngo.contactLegalFirstname ?? '',
			contactLegalLastname: ngo.contactLegalLastname ?? '',
			contactLegalPosition: ngo.contactLegalPosition ?? '',
			contactLegalId: ngo.contactLegalId ?? '',
			contactLegalEmail: ngo.contactLegalEmail ?? '',
			// Never pre-ticked: confirming is an act the person takes now, not a stored value (FR-70).
			legalRepresentativeConfirmed: false,
			language: ngo.language ?? 'SPA',

			certifiedByFoundation: ngo.certifiedByFoundation ?? false,
			certifiedByFoundationDescription: ngo.certifiedByFoundationDescription ?? '',
			corporateVolunteering: ngo.corporateVolunteering ?? false,
			corporateVolunteeringDescription: ngo.corporateVolunteeringDescription ?? '',

			donationCertificates: ngo.donationCertificates ?? false,
			bankInfo: {
				wantSwiftAndCC: ngo.bankInfo?.wantSwiftAndCC ?? false,
				bankSwiftCode: ngo.bankInfo?.bankSwiftCode ?? '',
				bankAccountNumber: ngo.bankInfo?.bankAccountNumber ?? '',
				bankIBANCode: ngo.bankInfo?.bankIBANCode ?? '',
			},
			bankCurrency: ngo.bankCurrency ?? 'EUR',
			bankAccountName: ngo.bankAccountName ?? '',
			bankAccountCertificate: undefined as File | '' | undefined,
			bankAddress: ngo.bankAddress ?? '',
			bankCity: ngo.bankCity ?? '',
			bankPostalCode: ngo.bankPostalCode ?? '',
			bankCountry: ngo.bankCountry ?? ngo.country ?? '',
			bankRegion: ngo.bankRegion ?? '',
			bankProvince: ngo.bankProvince ?? '',

			privacyEmail: ngo.privacyEmail ?? '',
			privacyPhone: ngo.privacyPhone ?? '',
			privacyAddress: ngo.privacyAddress ?? '',
			privacyCity: ngo.privacyCity ?? '',
			privacyPostalCode: ngo.privacyPostalCode ?? '',
			privacyCountry: ngo.privacyCountry ?? '',
			privacyRegion: ngo.privacyRegion ?? '',
			privacyProvince: ngo.privacyProvince ?? '',
		},
		onSubmit: async ({ value }) => {
			// The footer withholds the save button for this, so reaching here means the form was submitted
			// another way — the Enter key.
			if (awaitsConfirmation(value)) {
				toast.error(t`Tick the box confirming the legal representative's details before saving them.`);
				setTabsWithErrors((previous) => new Set(previous).add('contacts'));
				return;
			}

			let uploaded: Awaited<ReturnType<typeof uploadNgoFiles>>;
			try {
				uploaded = await uploadNgoFiles({ logo: value.logo, bankAccountCertificate: value.bankAccountCertificate });
			} catch (error: unknown) {
				// The browser reads the logo's size, so a non-image fails here; nothing else catches `onSubmit` (TEC-3763).
				if (error instanceof UnsupportedImageFileError) {
					toast.error(t`That file is not a valid image. Upload a PNG, JPG, GIF, WEBP or SVG file.`);
					return;
				}
				throw error;
			}

			const { legalRepresentativeConfirmed, ...settings } = value;
			const data: Record<string, unknown> = { ...settings };
			data.allowedEmails = value.allowedEmails.map((e) => e.email).filter((e) => e.trim() !== '');
			if (uploaded.logo) {
				data.logo = uploaded.logo.key;
				data.logoWidth = uploaded.logo.width;
				data.logoHeight = uploaded.logo.height;
			} else if (value.logo === '') {
				data.logo = '';
			} else {
				delete data.logo;
			}
			if (uploaded.bankAccountCertificate) {
				data.bankAccountCertificate = uploaded.bankAccountCertificate;
			} else if (value.bankAccountCertificate === '') {
				data.bankAccountCertificate = '';
			} else {
				delete data.bankAccountCertificate;
			}

			const result = await mutation.mutateAsync({ data });
			if (result && typeof result === 'object' && 'error' in result) {
				// Not a bank-details problem: the user picks another file where they are (TEC-3763).
				if (result.error === 'UNSUPPORTED_IMAGE_FILE') {
					toast.error(t`That file is not a valid image. Upload a PNG, JPG, GIF, WEBP or SVG file.`);
					return;
				}
				// The server is authoritative, including for a cross-tab save the client gate missed.
				const bankCertMessages: Record<string, string> = {
					BANK_CERTIFICATE_REMOVAL_FORBIDDEN: t`A bank ownership certificate is required. You can replace it, but you cannot remove it.`,
					BANK_CERTIFICATE_REQUIRED: t`Please upload a bank ownership certificate before saving.`,
					BANK_CERTIFICATE_REQUIRED_ON_CHANGE: t`Because you changed the IBAN or SWIFT/BIC, upload a new bank ownership certificate before saving.`,
				};
				const message =
					(typeof result.error === 'string' ? bankCertMessages[result.error] : undefined) ??
					t`Failed to save settings. Please try again.`;
				setTabsWithErrors((prev) => new Set(prev).add('donations'));
				navigate({ search: (prev) => ({ ...prev, tab: 'donations' }) });
				toast.error(message);
				return;
			}
			// After the save, so the designation is recorded against the values the NGO just stored.
			if (legalRepresentativeConfirmed) {
				try {
					await designate.mutateAsync({
						name: value.contactLegalFirstname,
						surname: value.contactLegalLastname,
						idDocument: value.contactLegalId,
						position: value.contactLegalPosition,
						email: value.contactLegalEmail,
					});
				} catch (error: unknown) {
					const refused = error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN';
					toast.error(
						refused
							? t`Only a contact of this organisation can confirm the legal representative.`
							: t`The settings were saved, but the legal representative could not be confirmed. Please try again.`,
					);
					setTabsWithErrors((prev) => new Set(prev).add('contacts'));
					return;
				}
			}

			await queryClient.invalidateQueries({ queryKey: trpc.ngo.getProfile.queryKey() });
			const hasContact2 = !!(value.contact2Firstname || value.contact2Lastname || value.contact2Email);
			setShowContact2(hasContact2);

			// https://github.com/TanStack/form/issues/1681
			setTimeout(() => {
				form.reset();

				navigate({ search: (prev) => ({ ...prev, editing: undefined }) });
			});
		},
	});

	useRegisterFormHandle(form);
	const uploadTexts = useUploadTexts();

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		const result = v.safeParse(settingsFormSchema, form.state.values);
		const errorTabsSet = new Set<TabValue>();
		if (!result.success) {
			for (const issue of result.issues) {
				const fieldName = String(issue.path?.[0]?.key);
				const tab = fieldToTab[fieldName];
				if (tab) errorTabsSet.add(tab);
			}
		}

		// The certificate rule needs the stored NGO values, which the static schema cannot see.
		const certRule = ngo.isDraft
			? null
			: evaluateBankCertificateRule({
					storedIBAN: ngo.bankInfo?.bankIBANCode,
					storedSwift: ngo.bankInfo?.bankSwiftCode,
					hasCertificateOnFile: !!ngo.bankAccountCertificate,
					submittedIBAN: form.state.values.bankInfo.bankIBANCode,
					submittedSwift: form.state.values.bankInfo.bankSwiftCode,
					certificateValue: form.state.values.bankAccountCertificate,
				});
		if (certRule) errorTabsSet.add('donations');

		setTabsWithErrors(errorTabsSet);

		if (errorTabsSet.size > 0 && !errorTabsSet.has(activeTab)) {
			const firstErrorTab = tabValues.find((t) => errorTabsSet.has(t));
			if (firstErrorTab) {
				navigate({
					search: (prev) => ({ ...prev, tab: firstErrorTab === 'organization' ? undefined : firstErrorTab }),
				});
				toast.error(t`Please fix the validation errors.`);
				return;
			}
		}

		form.handleSubmit();
	};

	return (
		<FormReadOnlyProvider value={!isEditing}>
			<VerticalTabs
				tabs={tabConfig}
				activeTab={activeTab}
				onTabChange={(value) =>
					navigate({
						search: (prev) => ({
							...prev,
							tab: value === 'organization' ? undefined : value,
						}),
					})
				}
			/>
			<div className="min-h-0 flex-1 overflow-y-auto pt-2 contain-layout md:overflow-y-scroll md:pt-4">
				<form
					id="settings-edit-form"
					onSubmit={handleSubmit}
					// The agreements list is a table of rows rather than a form, and the design gives it 748px (VSNI-78).
					className={`flex w-full ${activeTab === 'contracts' ? 'max-w-[812px]' : 'max-w-[600px]'} flex-col gap-6 px-4 pt-4 pb-28 md:px-8 md:pt-6 md:pb-32`}
				>
					{activeTab === 'organization' && (
						<>
							<Card className="shadow-warm">
								<CardHeader>
									<CardTitle className="text-xl">{t`Basic Information`}</CardTitle>
								</CardHeader>
								<CardContent className="flex flex-col gap-6">
									<form.AppField name="name" children={(field) => <field.TextField label={t`Entity name`} />} />
									<form.AppField
										name="web"
										children={(field) => (
											<field.LinkTextField label={t`Website`} placeholder="https://www.example.org" />
										)}
									/>
									<form.AppField name="taxId" children={(field) => <field.TextField label={t`Tax ID (CIF/NIF)`} />} />
									<form.AppField name="address" children={(field) => <field.TextField label={t`Address`} />} />
									<div className="flex gap-4">
										<form.AppField
											name="city"
											children={(field) => <field.TextField label={t`City`} className="w-full" />}
										/>
										<form.AppField
											name="postalCode"
											children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
										/>
									</div>
									<form.AppField
										name="country"
										listeners={{
											onChange: ({ value }) => {
												form.setFieldValue('region', '', { dontValidate: true, dontUpdateMeta: true });
												form.setFieldValue('province', '', { dontValidate: true, dontUpdateMeta: true });
												if (value) {
													const current = form.getFieldValue('countries');
													if (!current.includes(value)) {
														form.setFieldValue('countries', [...current, value]);
													}
												}
											},
										}}
										children={(field) => (
											<field.CountryField label={t`Country`} locale={getLocale(i18n)} texts={countryFieldTexts} />
										)}
									/>
									<form.Subscribe
										selector={(state) => ({ country: state.values.country, region: state.values.region })}
									>
										{({ country, region }) => (
											<>
												<form.AppField
													name="region"
													validators={{ onChangeListenTo: ['country'] }}
													listeners={{
														onChange: () =>
															form.setFieldValue('province', '', {
																dontValidate: true,
																dontUpdateMeta: true,
															}),
													}}
													children={(field) => (
														<field.RegionField label={t`Region`} country={country} texts={regionFieldTexts} />
													)}
												/>
												<form.AppField
													name="province"
													validators={{ onChangeListenTo: ['country', 'region'] }}
													children={(field) => (
														<field.ProvinceField
															label={t`Province`}
															region={region}
															country={country}
															texts={provinceFieldTexts}
														/>
													)}
												/>
											</>
										)}
									</form.Subscribe>
								</CardContent>
							</Card>

							<Card className="shadow-warm">
								<CardHeader>
									<CardTitle className="text-xl">{t`Multimedia`}</CardTitle>
								</CardHeader>
								<CardContent>
									<form.AppField
										name="logo"
										children={(field) => (
											<field.ImageUploadField
												{...uploadTexts.imageUpload()}
												currentUrl={ngo.logo}
												label={t`Logo`}
												buttonLabel={t`Upload Logo`}
												contain
												enableOpenInTab
											/>
										)}
									/>
								</CardContent>
							</Card>

							<Card className="shadow-warm mb-[20vh]">
								<CardHeader>
									<CardTitle className="text-xl">{t`Additional information`}</CardTitle>
								</CardHeader>
								<CardContent className="flex flex-col gap-6">
									<form.AppField
										name="privacyUrl"
										children={(field) => (
											<field.LinkTextField label={t`Privacy Policy URL`} placeholder="https://..." />
										)}
									/>
									<form.AppField
										name="actionScope"
										children={(field) => (
											<field.MultiComboboxTextField
												label={t`Action Scope`}
												search={t`Search…`}
												empty={t`No results found.`}
												options={actionScopeOptions}
											/>
										)}
									/>
									<form.AppField
										name="countries"
										children={(field) => (
											<field.MultiCountryField
												label={t`Countries where it operates`}
												locale={getLocale(i18n)}
												texts={countryFieldTexts}
											/>
										)}
									/>
									<form.AppField
										name="certifiedByFoundation"
										children={(field) => (
											<field.SwitchField
												label={t`Accreditation by an independent entity (e.g. Fundación Lealtad, Istituto Italiano della Donazione…)`}
												position="right"
											/>
										)}
									/>
									<form.Subscribe selector={(state) => state.values.certifiedByFoundation}>
										{(certifiedByFoundation) => (
											<div
												className="grid transition-[grid-template-rows,opacity] duration-200"
												style={{
													gridTemplateRows: certifiedByFoundation ? '1fr' : '0fr',
													opacity: certifiedByFoundation ? 1 : 0,
												}}
											>
												<div className="-m-1 overflow-hidden p-1">
													<form.AppField
														name="certifiedByFoundationDescription"
														children={(field) => (
															<field.TextareaField label={t`Name of the accrediting entity`} optional={false} />
														)}
													/>
												</div>
											</div>
										)}
									</form.Subscribe>
								</CardContent>
							</Card>
						</>
					)}

					{activeTab === 'contacts' && (
						<SettingsContactsTab
							form={form}
							isEditing={isEditing}
							inPlanB={ngo.hasEnteredPlanB}
							confirmation={{
								at: ngo.legalRepresentativeConfirmedAt,
								by: ngo.legalRepresentativeConfirmedBy,
								pending: ngo.awaitsLegalRepresentative,
								onRecord: {
									contactLegalFirstname: ngo.contactLegalFirstname ?? '',
									contactLegalLastname: ngo.contactLegalLastname ?? '',
									contactLegalPosition: ngo.contactLegalPosition ?? '',
									contactLegalId: ngo.contactLegalId ?? '',
									contactLegalEmail: ngo.contactLegalEmail ?? '',
								},
							}}
							showContact2={showContact2}
							setShowContact2={setShowContact2}
						/>
					)}

					{activeTab === 'users' && <SettingsUsersTab form={form} isEditing={isEditing} />}

					{activeTab === 'volunteering' && (
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Corporate volunteering`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<form.AppField
									name="corporateVolunteering"
									children={(field) => (
										<field.SwitchField label={t`Corporate volunteering offering`} position="right" />
									)}
								/>
								<form.Subscribe selector={(state) => state.values.corporateVolunteering}>
									{(corporateVolunteering) => (
										<div
											className="grid transition-[grid-template-rows,opacity] duration-200"
											style={{
												gridTemplateRows: corporateVolunteering ? '1fr' : '0fr',
												opacity: corporateVolunteering ? 1 : 0,
											}}
										>
											<div className="-m-1 overflow-hidden p-1">
												<form.AppField
													name="corporateVolunteeringDescription"
													children={(field) => (
														<field.TextareaField
															description={t`Describe how you offer corporate volunteering and add a web link to the volunteering activity or any other resource that allows us to keep it in our database in case a company asks us about it.`}
															optional={false}
														/>
													)}
												/>
											</div>
										</div>
									)}
								</form.Subscribe>
							</CardContent>
						</Card>
					)}

					{activeTab === 'privacy' && (
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Data protection officer`}</CardTitle>
							</CardHeader>
							<CardContent className="flex flex-col gap-6">
								<form.AppField
									name="privacyEmail"
									children={(field) => (
										<field.LinkTextField
											label={t`Data protection email`}
											placeholder="privacy@example.org"
											hrefTransform={(v) => `mailto:${v}`}
										/>
									)}
								/>
								<form.AppField
									name="privacyPhone"
									children={(field) => <field.TextField label={t`Phone`} placeholder="+34..." />}
								/>
								<form.AppField name="privacyAddress" children={(field) => <field.TextField label={t`Address`} />} />
								<div className="flex gap-4">
									<form.AppField
										name="privacyCity"
										children={(field) => <field.TextField label={t`City`} className="w-full" />}
									/>
									<form.AppField
										name="privacyPostalCode"
										children={(field) => <field.TextField label={t`Postal Code`} className="w-full" />}
									/>
								</div>
								<form.AppField
									name="privacyCountry"
									listeners={{
										onChange: () => {
											form.setFieldValue('privacyRegion', '', { dontValidate: true, dontUpdateMeta: true });
											form.setFieldValue('privacyProvince', '', { dontValidate: true, dontUpdateMeta: true });
										},
									}}
									children={(field) => (
										<field.CountryField label={t`Country`} locale={getLocale(i18n)} texts={countryFieldTexts} />
									)}
								/>
								<form.Subscribe
									selector={(state) => ({
										privacyCountry: state.values.privacyCountry,
										privacyRegion: state.values.privacyRegion,
									})}
								>
									{({ privacyCountry, privacyRegion }) => (
										<>
											<form.AppField
												name="privacyRegion"
												validators={{ onChangeListenTo: ['privacyCountry'] }}
												listeners={{
													onChange: () =>
														form.setFieldValue('privacyProvince', '', {
															dontValidate: true,
															dontUpdateMeta: true,
														}),
												}}
												children={(field) => (
													<field.RegionField label={t`Region`} country={privacyCountry} texts={regionFieldTexts} />
												)}
											/>
											<form.AppField
												name="privacyProvince"
												validators={{ onChangeListenTo: ['privacyCountry', 'privacyRegion'] }}
												children={(field) => (
													<field.ProvinceField
														label={t`Province`}
														region={privacyRegion}
														country={privacyCountry}
														texts={provinceFieldTexts}
													/>
												)}
											/>
										</>
									)}
								</form.Subscribe>
							</CardContent>
						</Card>
					)}

					{activeTab === 'donations' && <SettingsDonationsTab form={form} ngo={ngo} />}

					{activeTab === 'language' && (
						<Card className="shadow-warm">
							<CardHeader>
								<CardTitle className="text-xl">{t`Language`}</CardTitle>
								<CardDescription className="leading-relaxed">
									{t`Configure which language you want to view the NGO Area. This language selection has no effect on the languages ​​you have configured for your projects and portals.`}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<NgoLanguageSelect variant="field" />
							</CardContent>
						</Card>
					)}

					{activeTab === 'contracts' && <SettingsContractsTab />}
				</form>
			</div>
			{isEditing && (
				<form.Subscribe selector={(state) => awaitsConfirmation(state.values)}>
					{(blocked) => (
						<EditFooter
							form={form}
							formId="settings-edit-form"
							portalId="settings-footer-portal"
							onDiscard={() => navigate({ search: (prev) => ({ ...prev, editing: undefined }) })}
							blockedReason={blocked ? t`Confirm the legal representative's details above to save them.` : undefined}
						/>
					)}
				</form.Subscribe>
			)}
		</FormReadOnlyProvider>
	);
}
